/**
 * Scenario + Lens System
 * 
 * Core insight: Math depth comes from asking better questions 
 * about the SAME situation, not inventing new situations.
 * 
 * One familiar story → multiple mathematical lenses → years of learning
 */

// ============================================================================
// TYPES
// ============================================================================

export type MathLevel = 
  | 'elementary'      // Arithmetic, plug-and-chug
  | 'pre_algebra'     // Variables, simple equations
  | 'algebra_1'       // Linear functions, slope, two points
  | 'algebra_2'       // Quadratics, comparing models
  | 'pre_calc'        // Nonlinearity, rate of change concepts
  | 'calc_intro'      // Derivatives conceptually (no limits yet)
  | 'calc_1'          // Formal derivatives, optimization
  | 'calc_2';         // Integrals, accumulation

export type QuestionLens = 
  | 'evaluate'            // "How many coins after 10 minutes?"
  | 'write_equation'      // "Write an equation for coins vs time"
  | 'slope'               // "What is the slope? What does it mean?"
  | 'difference'          // "How many MORE between t=3 and t=7?"
  | 'average_rate'        // "Average rate of change between two points"
  | 'is_constant'         // "Is the rate constant? How do you know?"
  | 'instantaneous_rate'  // "How fast RIGHT NOW at t=4?"
  | 'compare'             // "Which earning rate is better?"
  | 'optimize'            // "When does the upgrade pay off?"
  | 'accumulate';         // "Total coins earned over the interval"

export interface Scenario {
  id: string;
  name: string;
  context: string;           // "garden economy", "racing game", etc.
  description: string;       // Kid-friendly description
  
  // The mathematical model
  baseFunction: string;      // e.g., "C(t) = 5t^2 + 10t"
  simplifiedFunction?: string; // Linear version for lower levels: "C(t) = 10t"
  
  // Variable meanings (for story coherence)
  variables: {
    independent: { symbol: string; meaning: string; unit: string };
    dependent: { symbol: string; meaning: string; unit: string };
  };
  
  // Visual/thematic elements
  theme: {
    emoji: string;
    color: string;
    iconUrl?: string;
  };
  
  // Which lenses are available for this scenario
  availableLenses: QuestionLens[];
}

export interface LensConfig {
  lens: QuestionLens;
  name: string;
  description: string;
  minLevel: MathLevel;
  maxLevel: MathLevel;
  
  // Question generation
  questionTemplates: string[];
  
  // What the student is really learning
  conceptualGoal: string;
  
  // Grading approach
  gradingType: 'exact' | 'numeric' | 'expression' | 'explanation';
}

export interface GeneratedQuestion {
  id: string;
  scenarioId: string;
  lens: QuestionLens;
  level: MathLevel;
  
  // The question itself
  question: string;
  context: string;          // Story setup
  
  // Function used (may be simplified for lower levels)
  functionUsed: string;
  functionLatex: string;
  
  // Answer
  expectedAnswer: string;
  acceptableAnswers: string[];
  
  // For grading
  workingSteps?: string[];
  
  // Metadata
  xpReward: number;
  estimatedTimeSeconds: number;
}

// ============================================================================
// SCENARIOS
// ============================================================================

export const SCENARIOS: Record<string, Scenario> = {
  'garden_economy': {
    id: 'garden_economy',
    name: 'Garden Coins',
    context: 'garden economy',
    description: 'Your magical garden grows coins over time. The longer you wait, the more you earn!',
    
    baseFunction: 'C(t) = 5t² + 10t',
    simplifiedFunction: 'C(t) = 10t',
    
    variables: {
      independent: { symbol: 't', meaning: 'time', unit: 'minutes' },
      dependent: { symbol: 'C', meaning: 'coins', unit: 'coins' },
    },
    
    theme: {
      emoji: '🌱',
      color: '#7fff00',
    },
    
    availableLenses: [
      'evaluate', 'write_equation', 'slope', 'difference', 
      'average_rate', 'is_constant', 'instantaneous_rate', 'compare', 'optimize'
    ],
  },
  
  'racing_speed': {
    id: 'racing_speed',
    name: 'Turbo Racer',
    context: 'racing game',
    description: 'Your car accelerates down the track. Position changes as you race!',
    
    baseFunction: 'D(t) = 4t² + 2t',
    simplifiedFunction: 'D(t) = 20t',
    
    variables: {
      independent: { symbol: 't', meaning: 'time', unit: 'seconds' },
      dependent: { symbol: 'D', meaning: 'distance', unit: 'meters' },
    },
    
    theme: {
      emoji: '🏎️',
      color: '#ef4444',
    },
    
    availableLenses: [
      'evaluate', 'write_equation', 'slope', 'difference',
      'average_rate', 'is_constant', 'instantaneous_rate', 'compare'
    ],
  },
  
  'tower_height': {
    id: 'tower_height',
    name: 'Tower Builder',
    context: 'tower building game',
    description: 'Stack blocks to build the tallest tower. Each level adds more height!',
    
    baseFunction: 'H(n) = 3n² + 5n + 2',
    simplifiedFunction: 'H(n) = 8n',
    
    variables: {
      independent: { symbol: 'n', meaning: 'blocks placed', unit: 'blocks' },
      dependent: { symbol: 'H', meaning: 'tower height', unit: 'units' },
    },
    
    theme: {
      emoji: '🏗️',
      color: '#ffd700',
    },
    
    availableLenses: [
      'evaluate', 'write_equation', 'slope', 'difference',
      'average_rate', 'is_constant', 'instantaneous_rate'
    ],
  },
  
  'xp_grind': {
    id: 'xp_grind',
    name: 'XP Grinder',
    context: 'RPG leveling',
    description: 'Defeat monsters to earn XP. Some give bonus XP as you level up!',
    
    baseFunction: 'X(m) = 2m² + 15m',
    simplifiedFunction: 'X(m) = 25m',
    
    variables: {
      independent: { symbol: 'm', meaning: 'monsters defeated', unit: 'monsters' },
      dependent: { symbol: 'X', meaning: 'experience points', unit: 'XP' },
    },
    
    theme: {
      emoji: '⚔️',
      color: '#a855f7',
    },
    
    availableLenses: [
      'evaluate', 'write_equation', 'slope', 'difference',
      'average_rate', 'is_constant', 'instantaneous_rate', 'compare', 'optimize'
    ],
  },
  
  'pet_growth': {
    id: 'pet_growth',
    name: 'Pet Power',
    context: 'pet simulator',
    description: 'Feed your pet to make it stronger. Its power grows as it eats!',
    
    baseFunction: 'P(f) = f² + 4f + 5',
    simplifiedFunction: 'P(f) = 5f + 5',
    
    variables: {
      independent: { symbol: 'f', meaning: 'food given', unit: 'treats' },
      dependent: { symbol: 'P', meaning: 'pet power', unit: 'power' },
    },
    
    theme: {
      emoji: '🐾',
      color: '#ec4899',
    },
    
    availableLenses: [
      'evaluate', 'write_equation', 'slope', 'difference',
      'average_rate', 'is_constant', 'instantaneous_rate'
    ],
  },
};

// ============================================================================
// LENS CONFIGURATIONS
// ============================================================================

export const LENS_CONFIGS: Record<QuestionLens, LensConfig> = {
  evaluate: {
    lens: 'evaluate',
    name: 'Calculate Value',
    description: 'Plug in a number and compute the result',
    minLevel: 'elementary',
    maxLevel: 'algebra_1',
    questionTemplates: [
      'How many {dependent} do you have after {value} {independent_unit}?',
      'If {independent} = {value}, what is {dependent}?',
      'Calculate your {dependent} when {independent} is {value}.',
    ],
    conceptualGoal: 'Function evaluation, arithmetic with expressions',
    gradingType: 'numeric',
  },
  
  write_equation: {
    lens: 'write_equation',
    name: 'Write the Equation',
    description: 'Express the relationship as a mathematical equation',
    minLevel: 'pre_algebra',
    maxLevel: 'algebra_2',
    questionTemplates: [
      'Write an equation that shows how {dependent} depends on {independent}.',
      'Express {dependent} as a function of {independent}.',
      'What equation describes this relationship?',
    ],
    conceptualGoal: 'Translating patterns into algebraic form',
    gradingType: 'expression',
  },
  
  slope: {
    lens: 'slope',
    name: 'Find the Slope',
    description: 'Calculate and interpret the slope',
    minLevel: 'algebra_1',
    maxLevel: 'algebra_2',
    questionTemplates: [
      'What is the slope? What does it mean in this game?',
      'Find the rate of change. What does this number tell you?',
      'Calculate the slope and explain what it represents.',
    ],
    conceptualGoal: 'Slope as rate of change, real-world meaning',
    gradingType: 'numeric',
  },
  
  difference: {
    lens: 'difference',
    name: 'Find the Difference',
    description: 'Calculate change between two points',
    minLevel: 'algebra_1',
    maxLevel: 'algebra_2',
    questionTemplates: [
      'How many MORE {dependent} do you earn between {independent} = {value1} and {independent} = {value2}?',
      'What is the change in {dependent} from {value1} to {value2} {independent_unit}?',
      'Calculate {dependent}({value2}) - {dependent}({value1}).',
    ],
    conceptualGoal: 'Distance between points, Δy calculation',
    gradingType: 'numeric',
  },
  
  average_rate: {
    lens: 'average_rate',
    name: 'Average Rate of Change',
    description: 'Find the average rate between two points',
    minLevel: 'algebra_2',
    maxLevel: 'pre_calc',
    questionTemplates: [
      'What is the average rate of change from {independent} = {value1} to {independent} = {value2}?',
      'On average, how many {dependent} per {independent_unit} between {value1} and {value2}?',
      'Calculate (Δ{dependent})/(Δ{independent}) from {value1} to {value2}.',
    ],
    conceptualGoal: 'Slope between two points, average vs instantaneous',
    gradingType: 'numeric',
  },
  
  is_constant: {
    lens: 'is_constant',
    name: 'Is It Constant?',
    description: 'Determine if the rate of change is constant',
    minLevel: 'algebra_2',
    maxLevel: 'pre_calc',
    questionTemplates: [
      'Is the rate of earning {dependent} constant? How do you know?',
      'Does {dependent} grow at the same rate every {independent_unit}? Explain.',
      'Is this a linear relationship? Why or why not?',
    ],
    conceptualGoal: 'Linear vs nonlinear, acceleration concept',
    gradingType: 'explanation',
  },
  
  instantaneous_rate: {
    lens: 'instantaneous_rate',
    name: 'Rate Right Now',
    description: 'Find the instantaneous rate of change (derivative)',
    minLevel: 'calc_intro',
    maxLevel: 'calc_1',
    questionTemplates: [
      'How fast is {dependent} increasing RIGHT NOW at {independent} = {value}?',
      'What is the rate of change at the exact moment when {independent} = {value}?',
      'Find the instantaneous rate at {independent} = {value}.',
    ],
    conceptualGoal: 'Derivative as instantaneous rate, not slope between points',
    gradingType: 'numeric',
  },
  
  compare: {
    lens: 'compare',
    name: 'Compare Strategies',
    description: 'Compare two different functions or strategies',
    minLevel: 'algebra_1',
    maxLevel: 'calc_1',
    questionTemplates: [
      'Strategy A gives {func1}. Strategy B gives {func2}. Which is better after {value} {independent_unit}?',
      'Compare these two earning rates. When is each one better?',
      'At what point do these two strategies give the same result?',
    ],
    conceptualGoal: 'Comparing functions, intersection points',
    gradingType: 'explanation',
  },
  
  optimize: {
    lens: 'optimize',
    name: 'Find the Best',
    description: 'Find optimal values or break-even points',
    minLevel: 'algebra_2',
    maxLevel: 'calc_1',
    questionTemplates: [
      'An upgrade costs {cost} {dependent}. When does it pay off?',
      'What {independent} value maximizes your {dependent} per {independent_unit}?',
      'Find the break-even point for this investment.',
    ],
    conceptualGoal: 'Optimization, break-even analysis',
    gradingType: 'numeric',
  },
  
  accumulate: {
    lens: 'accumulate',
    name: 'Total Accumulated',
    description: 'Find total accumulated value (integral concept)',
    minLevel: 'calc_1',
    maxLevel: 'calc_2',
    questionTemplates: [
      'What is the TOTAL {dependent} earned from {independent} = {value1} to {independent} = {value2}?',
      'Calculate the accumulated {dependent} over the interval [{value1}, {value2}].',
      'Find the area under the curve from {value1} to {value2}.',
    ],
    conceptualGoal: 'Integration as accumulation, area under curve',
    gradingType: 'numeric',
  },
};

// ============================================================================
// LEVEL PROGRESSION
// ============================================================================

export const LEVEL_ORDER: MathLevel[] = [
  'elementary',
  'pre_algebra', 
  'algebra_1',
  'algebra_2',
  'pre_calc',
  'calc_intro',
  'calc_1',
  'calc_2',
];

export const LEVEL_INFO: Record<MathLevel, {
  name: string;
  gradeRange: string;
  description: string;
  xpMultiplier: number;
}> = {
  elementary: {
    name: 'Elementary',
    gradeRange: 'K-5',
    description: 'Arithmetic and basic patterns',
    xpMultiplier: 1.0,
  },
  pre_algebra: {
    name: 'Pre-Algebra',
    gradeRange: '6-7',
    description: 'Variables and simple equations',
    xpMultiplier: 1.2,
  },
  algebra_1: {
    name: 'Algebra I',
    gradeRange: '8-9',
    description: 'Linear functions and slope',
    xpMultiplier: 1.5,
  },
  algebra_2: {
    name: 'Algebra II',
    gradeRange: '9-10',
    description: 'Quadratics and model comparison',
    xpMultiplier: 1.8,
  },
  pre_calc: {
    name: 'Pre-Calculus',
    gradeRange: '10-11',
    description: 'Nonlinearity and rate concepts',
    xpMultiplier: 2.0,
  },
  calc_intro: {
    name: 'Intro Calculus',
    gradeRange: '11-12',
    description: 'Derivatives without limits',
    xpMultiplier: 2.5,
  },
  calc_1: {
    name: 'Calculus I',
    gradeRange: '12+',
    description: 'Formal derivatives and optimization',
    xpMultiplier: 3.0,
  },
  calc_2: {
    name: 'Calculus II',
    gradeRange: 'College',
    description: 'Integration and accumulation',
    xpMultiplier: 3.5,
  },
};

// ============================================================================
// QUESTION GENERATOR
// ============================================================================

/**
 * Generate a question for a scenario at a specific level with a specific lens
 */
export function generateQuestion(params: {
  scenarioId: string;
  lens: QuestionLens;
  level: MathLevel;
  seed?: number;
}): GeneratedQuestion | null {
  const { scenarioId, lens, level, seed = Date.now() } = params;
  
  const scenario = SCENARIOS[scenarioId];
  const lensConfig = LENS_CONFIGS[lens];
  
  if (!scenario || !lensConfig) return null;
  
  // Check if lens is available for this scenario
  if (!scenario.availableLenses.includes(lens)) return null;
  
  // Check if level is appropriate for this lens
  const levelIndex = LEVEL_ORDER.indexOf(level);
  const minIndex = LEVEL_ORDER.indexOf(lensConfig.minLevel);
  const maxIndex = LEVEL_ORDER.indexOf(lensConfig.maxLevel);
  
  if (levelIndex < minIndex || levelIndex > maxIndex) return null;
  
  // Determine which function to use
  const useSimplified = levelIndex <= LEVEL_ORDER.indexOf('algebra_1') && scenario.simplifiedFunction;
  const functionUsed = useSimplified ? scenario.simplifiedFunction! : scenario.baseFunction;
  
  // Generate random values based on seed
  const rng = seededRandom(seed);
  const values = generateValues(rng, lens);
  
  // Build the question
  const question = buildQuestion(scenario, lensConfig, functionUsed, values, level);
  
  // Calculate expected answer
  const { answer, steps } = calculateAnswer(functionUsed, lens, values, scenario.variables);
  
  // Calculate XP reward
  const baseXP = 20;
  const levelMultiplier = LEVEL_INFO[level].xpMultiplier;
  const xpReward = Math.round(baseXP * levelMultiplier);
  
  return {
    id: `q_${scenarioId}_${lens}_${seed}`,
    scenarioId,
    lens,
    level,
    question: question.text,
    context: question.context,
    functionUsed,
    functionLatex: toLatex(functionUsed),
    expectedAnswer: answer,
    acceptableAnswers: generateAcceptableAnswers(answer, lens),
    workingSteps: steps,
    xpReward,
    estimatedTimeSeconds: getEstimatedTime(lens, level),
  };
}

/**
 * Get all available lenses for a student at a given level
 */
export function getAvailableLenses(level: MathLevel): QuestionLens[] {
  const levelIndex = LEVEL_ORDER.indexOf(level);
  
  return Object.entries(LENS_CONFIGS)
    .filter(([_, config]) => {
      const minIndex = LEVEL_ORDER.indexOf(config.minLevel);
      const maxIndex = LEVEL_ORDER.indexOf(config.maxLevel);
      return levelIndex >= minIndex && levelIndex <= maxIndex;
    })
    .map(([lens]) => lens as QuestionLens);
}

/**
 * Get the learning arc for a scenario - shows full K-12 progression
 */
export function getLearningArc(scenarioId: string): Array<{
  level: MathLevel;
  levelInfo: typeof LEVEL_INFO[MathLevel];
  availableLenses: Array<{
    lens: QuestionLens;
    config: LensConfig;
  }>;
}> {
  const scenario = SCENARIOS[scenarioId];
  if (!scenario) return [];
  
  return LEVEL_ORDER.map(level => {
    const lenses = getAvailableLenses(level)
      .filter(lens => scenario.availableLenses.includes(lens))
      .map(lens => ({
        lens,
        config: LENS_CONFIGS[lens],
      }));
    
    return {
      level,
      levelInfo: LEVEL_INFO[level],
      availableLenses: lenses,
    };
  }).filter(item => item.availableLenses.length > 0);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function generateValues(rng: () => number, lens: QuestionLens): Record<string, number> {
  switch (lens) {
    case 'evaluate':
      return { value: Math.floor(rng() * 10) + 1 };
    
    case 'difference':
    case 'average_rate':
    case 'accumulate':
      const v1 = Math.floor(rng() * 5) + 1;
      return { value1: v1, value2: v1 + Math.floor(rng() * 5) + 2 };
    
    case 'instantaneous_rate':
      return { value: Math.floor(rng() * 8) + 2 };
    
    case 'compare':
      return { value: Math.floor(rng() * 10) + 5 };
    
    case 'optimize':
      return { cost: (Math.floor(rng() * 5) + 1) * 50 };
    
    default:
      return { value: Math.floor(rng() * 10) + 1 };
  }
}

function buildQuestion(
  scenario: Scenario,
  lensConfig: LensConfig,
  functionUsed: string,
  values: Record<string, number>,
  _level: MathLevel
): { text: string; context: string } {
  // Pick a random template
  const template = lensConfig.questionTemplates[0]; // Could randomize
  
  // Replace placeholders
  let text = template
    .replace(/{dependent}/g, scenario.variables.dependent.meaning)
    .replace(/{independent}/g, scenario.variables.independent.symbol)
    .replace(/{independent_unit}/g, scenario.variables.independent.unit)
    .replace(/{value}/g, String(values.value || ''))
    .replace(/{value1}/g, String(values.value1 || ''))
    .replace(/{value2}/g, String(values.value2 || ''))
    .replace(/{cost}/g, String(values.cost || ''));
  
  // Build context
  const context = `${scenario.theme.emoji} ${scenario.description}\n\nThe formula is: ${functionUsed}`;
  
  return { text, context };
}

function calculateAnswer(
  func: string,
  lens: QuestionLens,
  values: Record<string, number>,
  variables: Scenario['variables']
): { answer: string; steps: string[] } {
  // Parse the function (simplified parsing for common forms)
  const parsed = parseFunction(func);
  const steps: string[] = [];
  
  switch (lens) {
    case 'evaluate': {
      const t = values.value;
      const result = evaluateFunction(parsed, t);
      steps.push(`Substitute ${variables.independent.symbol} = ${t}`);
      steps.push(`${func.split('=')[0].trim()}(${t}) = ${result}`);
      return { answer: String(result), steps };
    }
    
    case 'difference': {
      const r1 = evaluateFunction(parsed, values.value1);
      const r2 = evaluateFunction(parsed, values.value2);
      const diff = r2 - r1;
      steps.push(`Calculate at ${variables.independent.symbol} = ${values.value1}: ${r1}`);
      steps.push(`Calculate at ${variables.independent.symbol} = ${values.value2}: ${r2}`);
      steps.push(`Difference: ${r2} - ${r1} = ${diff}`);
      return { answer: String(diff), steps };
    }
    
    case 'average_rate': {
      const r1 = evaluateFunction(parsed, values.value1);
      const r2 = evaluateFunction(parsed, values.value2);
      const avgRate = (r2 - r1) / (values.value2 - values.value1);
      steps.push(`Δ${variables.dependent.symbol} = ${r2} - ${r1} = ${r2 - r1}`);
      steps.push(`Δ${variables.independent.symbol} = ${values.value2} - ${values.value1} = ${values.value2 - values.value1}`);
      steps.push(`Average rate = ${r2 - r1} / ${values.value2 - values.value1} = ${avgRate}`);
      return { answer: String(avgRate), steps };
    }
    
    case 'instantaneous_rate': {
      const t = values.value;
      const derivative = evaluateDerivative(parsed, t);
      steps.push(`Take the derivative of ${func}`);
      steps.push(`Evaluate at ${variables.independent.symbol} = ${t}`);
      steps.push(`Rate = ${derivative} ${variables.dependent.unit}/${variables.independent.unit}`);
      return { answer: String(derivative), steps };
    }
    
    case 'slope': {
      // For linear functions, slope is the coefficient of the variable
      const slope = parsed.linear || 0;
      steps.push(`The slope is the coefficient of ${variables.independent.symbol}`);
      steps.push(`Slope = ${slope}`);
      return { answer: String(slope), steps };
    }
    
    default:
      return { answer: '0', steps: [] };
  }
}

interface ParsedFunction {
  quadratic: number;  // coefficient of t²
  linear: number;     // coefficient of t
  constant: number;   // constant term
}

function parseFunction(func: string): ParsedFunction {
  // Simple parser for forms like "C(t) = 5t² + 10t" or "C(t) = 10t"
  const rhs = func.split('=')[1] || func;
  
  let quadratic = 0, linear = 0, constant = 0;
  
  // Match quadratic term: coefficient followed by variable squared
  const quadMatch = rhs.match(/([+-]?\s*\d*)\s*[a-z]\s*[²\^2]/i);
  if (quadMatch) {
    const coef = quadMatch[1].replace(/\s/g, '') || '1';
    quadratic = coef === '+' || coef === '' ? 1 : coef === '-' ? -1 : parseInt(coef);
  }
  
  // Match linear term: coefficient followed by variable (not squared)
  const linMatch = rhs.match(/([+-]?\s*\d+)\s*[a-z](?![²\^])/i);
  if (linMatch) {
    linear = parseInt(linMatch[1].replace(/\s/g, ''));
  }
  
  // Match constant term
  const constMatch = rhs.match(/([+-]?\s*\d+)(?![a-z²\^])\s*$/i);
  if (constMatch && !constMatch[1].match(/[a-z]/i)) {
    constant = parseInt(constMatch[1].replace(/\s/g, ''));
  }
  
  return { quadratic, linear, constant };
}

function evaluateFunction(parsed: ParsedFunction, t: number): number {
  return parsed.quadratic * t * t + parsed.linear * t + parsed.constant;
}

function evaluateDerivative(parsed: ParsedFunction, t: number): number {
  // Derivative of at² + bt + c is 2at + b
  return 2 * parsed.quadratic * t + parsed.linear;
}

function toLatex(func: string): string {
  return func
    .replace(/\^2/g, '^{2}')
    .replace(/²/g, '^{2}')
    .replace(/\*/g, ' \\cdot ');
}

function generateAcceptableAnswers(answer: string, _lens: QuestionLens): string[] {
  const answers = [answer];
  
  // Add numeric variations
  const num = parseFloat(answer);
  if (!isNaN(num)) {
    answers.push(num.toFixed(0));
    answers.push(num.toFixed(1));
    answers.push(num.toFixed(2));
    if (num === Math.floor(num)) {
      answers.push(String(Math.floor(num)));
    }
  }
  
  return [...new Set(answers)];
}

function getEstimatedTime(lens: QuestionLens, level: MathLevel): number {
  const baseTime: Record<QuestionLens, number> = {
    evaluate: 30,
    write_equation: 60,
    slope: 45,
    difference: 60,
    average_rate: 90,
    is_constant: 90,
    instantaneous_rate: 120,
    compare: 120,
    optimize: 180,
    accumulate: 180,
  };
  
  const levelMultiplier = 1 + LEVEL_ORDER.indexOf(level) * 0.1;
  
  return Math.round((baseTime[lens] || 60) * levelMultiplier);
}

// ============================================================================
// EXPORTS FOR PARENT DASHBOARD
// ============================================================================

export function getScenarioProgress(
  scenarioId: string,
  completedQuestions: Array<{ lens: QuestionLens; level: MathLevel; score: number }>
): {
  currentLevel: MathLevel;
  masteredLenses: QuestionLens[];
  nextLenses: QuestionLens[];
  progressPercent: number;
} {
  const scenario = SCENARIOS[scenarioId];
  if (!scenario) {
    return {
      currentLevel: 'elementary',
      masteredLenses: [],
      nextLenses: [],
      progressPercent: 0,
    };
  }
  
  // Find highest level with ≥80% on any lens
  const levelScores: Map<MathLevel, number[]> = new Map();
  
  for (const q of completedQuestions) {
    const scores = levelScores.get(q.level) || [];
    scores.push(q.score);
    levelScores.set(q.level, scores);
  }
  
  let currentLevel: MathLevel = 'elementary';
  for (const level of LEVEL_ORDER) {
    const scores = levelScores.get(level) || [];
    const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    if (avg >= 0.8 && scores.length >= 2) {
      currentLevel = level;
    }
  }
  
  const masteredLenses = completedQuestions
    .filter(q => q.score >= 0.8)
    .map(q => q.lens);
  
  const availableLenses = getAvailableLenses(currentLevel)
    .filter(l => scenario.availableLenses.includes(l));
  
  const nextLenses = availableLenses.filter(l => !masteredLenses.includes(l));
  
  const totalLenses = scenario.availableLenses.length * LEVEL_ORDER.length;
  const completedCount = completedQuestions.filter(q => q.score >= 0.8).length;
  const progressPercent = Math.round((completedCount / totalLenses) * 100);
  
  return {
    currentLevel,
    masteredLenses: [...new Set(masteredLenses)],
    nextLenses,
    progressPercent,
  };
}
